import { useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material';

import { displayedText } from 'utils/dom';
import type { ColumnOverflowMode } from 'types/formItem';

interface Props {
  children: ReactNode;
  mode: ColumnOverflowMode;
  sx?: SxProps<Theme>;
}

export const AgendaCellContent = ({ children, mode, sx }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [tooltip, setTooltip] = useState('');

  const wrap = mode === 'Wrap';
  const textOverflow = wrap ? 'clip' : 'ellipsis';
  const whiteSpace = wrap ? 'normal' : 'nowrap';

  useEffect(() => {
    const element = ref.current;

    if (!active || wrap || !element) return;

    const updateTooltip = () => {
      const next = displayedText(element).trim();
      setTooltip((current) => (current === next ? current : next));
    };

    updateTooltip();

    const observer = new MutationObserver(updateTooltip);

    observer.observe(element, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['value'],
    });

    element.addEventListener('input', updateTooltip);
    element.addEventListener('change', updateTooltip);

    return () => {
      observer.disconnect();
      element.removeEventListener('input', updateTooltip);
      element.removeEventListener('change', updateTooltip);
    };
  }, [active, wrap]);

  return (
    <Tooltip
      title={wrap ? '' : tooltip}
      open={!wrap && active && Boolean(tooltip)}
      onClose={() => setActive(false)}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      describeChild
      slotProps={{ tooltip: { sx: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } } }}>
      <Box
        ref={ref}
        data-column-overflow={mode}
        onMouseEnter={() => setActive(true)}
        onMouseLeave={() => setActive(false)}
        onFocusCapture={() => setActive(true)}
        onBlurCapture={() => setActive(false)}
        sx={[
          ...(Array.isArray(sx) ? sx : [sx ?? {}]),
          {
            minWidth: 0,
            maxWidth: '100%',
            whiteSpace,
            overflow: 'hidden',
            '&:has(.MuiFormHelperText-root), &:has(.field-validation-message)': {
              overflow: 'visible',
            },
            textOverflow,
            overflowWrap: wrap ? 'anywhere' : 'normal',
            '& :is(div, span, p, a)': { minWidth: 0, maxWidth: '100%', whiteSpace, textOverflow, overflowWrap: 'inherit' },
            '& .MuiTypography-root, & .MuiSelect-select': { whiteSpace, textOverflow, overflow: wrap ? 'visible' : 'hidden' },
            '& input': { minWidth: 0, textOverflow },
            '& div:not(.MuiInputBase-root)': { flexWrap: wrap ? 'wrap' : 'nowrap' },
            '& svg': { flexShrink: 0 },
          },
        ]}>
        {children}
      </Box>
    </Tooltip>
  );
};
